import { jsx as _jsx } from "react/jsx-runtime";
/* eslint-disable max-len */
import { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { useStore } from '@mcn-platform/models';
import { EmbeddedConversation, Conversation, sharedConversationMeta, ConversationStoreProvider, SelectionWizardProvider, } from '@mcn-platform/converse-lib';
import ConversationStoreAdapter from './ConversationStoreAdapter';
import assetGetData from '../common/asset/assetGetData';
const EmbeddedConversationWidget = ({ headerLogo, headerLogoLocalized, headerLogoL10N, minimizedLogo, minimizedLogoLocalized, minimizedLogoL10N, logoPosition, headerHeight = 50, menuButtonPosition, minimizeButtonPosition, closeButtonPosition, showItemReferencesWithMessage = false, showPersistentPrompts, showResetWithInput, courseCardShowImage, courseCardShowTitle, courseCardShowInstructor, courseCardShowDescLines, openWebPagesInNewTab, bodyStyle, inputStyle, startingMessageFontStyle, windowStyle, questionStyle, responseStyle, feedbackButton, questionAlignment, responseAlignment, referenceFormatting, headerStyle, headerControlsColor, dividerStyle, footerStyle, footerLinks, footerText, welcomeScreenShellStyle, adminOptions, sendImage, enableVoiceInput, agentAvatarImage, userAvatarImage, resetConversationLabel, hideRightRailOnMobile, enableMultiRefinements, multiRefinementSubmitButtonText, clientAIAnimatedProcessingIcon, maxItemsInRightRail, productFormatting, defaultProcessingIcon, agentName, productPromptHeader, }) => {
    const [ready, setReady] = useState(false);
    const [logoImage, setLogoImage] = useState(undefined);
    const [minimizedLogoImage, setMinimizedLogoImage] = useState(undefined);
    const [clientAIAnimatedProcessingIconImage, setClientAIAnimatedProcessingIconImage] = useState(undefined);
    const [agentAvatarImageImage, setAgentAvatarImageImage] = useState(undefined);
    const [userAvatarImageImage, setUserAvatarImageImage] = useState(undefined);
    const [sendImageImage, setSendImageImage] = useState(undefined);
    const store = useStore();
    const navigate = useNavigate();
    const onPortalRelativeNavigate = useCallback((relativePath) => {
        if (relativePath)
            navigate(relativePath);
    }, [navigate]);
    const locale = store.customerContext?.currentPage?.locale
        || store.customerContext?.locale;
    const defaultLocale = store.currentPortal?.defaultLocale || 'en';
    useEffect(() => {
        const load = async () => {
            const resolvedHeaderLogo = resolveLocalizedAssetId({
                legacyId: headerLogo,
                localizedMap: headerLogoL10N,
                enabled: Boolean(headerLogoLocalized),
                locale,
                defaultLocale,
            });
            const resolvedMinimizedLogo = resolveLocalizedAssetId({
                legacyId: minimizedLogo,
                localizedMap: minimizedLogoL10N,
                enabled: Boolean(minimizedLogoLocalized),
                locale,
                defaultLocale,
            });
            const data = await EmbeddedConversationWidget.getData({
                headerLogo: resolvedHeaderLogo,
                minimizedLogo: resolvedMinimizedLogo,
                clientAIAnimatedProcessingIcon,
                agentAvatarImage,
                userAvatarImage,
                sendImage,
            }, store);
            setLogoImage(data.headerLogo);
            setMinimizedLogoImage(data.minimizedLogo);
            setClientAIAnimatedProcessingIconImage(data.clientAIAnimatedProcessingIcon);
            setAgentAvatarImageImage(data.agentAvatarImage);
            setUserAvatarImageImage(data.userAvatarImage);
            setSendImageImage(data.sendImage);
            setReady(true);
            return data;
        };
        load();
    }, [
        headerLogo,
        headerLogoLocalized,
        headerLogoL10N,
        minimizedLogo,
        minimizedLogoLocalized,
        minimizedLogoL10N,
        clientAIAnimatedProcessingIcon,
        agentAvatarImage,
        userAvatarImage,
        sendImage,
        locale,
        defaultLocale,
        store,
    ]);
    if (!ready) {
        return null;
    }
    return (_jsx(ConversationStoreProvider, { value: new ConversationStoreAdapter(store), children: _jsx(SelectionWizardProvider, { children: _jsx(EmbeddedConversation, { headerLogo: logoImage, minimizedLogo: minimizedLogoImage, logoPosition: logoPosition, headerHeight: headerHeight, menuButtonPosition: menuButtonPosition, minimizeButtonPosition: minimizeButtonPosition, closeButtonPosition: closeButtonPosition, showItemReferencesWithMessage: showItemReferencesWithMessage, showPersistentPrompts: showPersistentPrompts !== false, showResetWithInput: showResetWithInput, courseCardShowImage: courseCardShowImage, courseCardShowTitle: courseCardShowTitle, courseCardShowInstructor: courseCardShowInstructor, courseCardShowDescLines: courseCardShowDescLines, openWebPagesInNewTab: openWebPagesInNewTab, windowStyle: windowStyle, bodyStyle: bodyStyle, inputStyle: inputStyle, startingMessageFontStyle: startingMessageFontStyle, questionStyle: questionStyle, responseStyle: responseStyle, feedbackButton: feedbackButton, questionAlignment: questionAlignment, responseAlignment: responseAlignment, referenceFormatting: referenceFormatting, headerStyle: headerStyle, headerControlsColor: headerControlsColor, dividerStyle: dividerStyle, footerStyle: footerStyle, footerLinks: footerLinks, footerText: footerText, welcomeScreenShellStyle: welcomeScreenShellStyle, adminOptions: adminOptions, sendImage: sendImageImage, enableVoiceInput: enableVoiceInput, agentAvatarImage: agentAvatarImageImage, userAvatarImage: userAvatarImageImage, resetConversationLabel: resetConversationLabel, hideRightRailOnMobile: hideRightRailOnMobile, enableMultiRefinements: enableMultiRefinements, multiRefinementSubmitButtonText: multiRefinementSubmitButtonText, animatedProcessingImage: clientAIAnimatedProcessingIconImage, maxItemsInRightRail: maxItemsInRightRail, productFormatting: productFormatting, defaultProcessingIcon: defaultProcessingIcon, agentName: agentName, productPromptHeader: productPromptHeader, onPortalRelativeNavigate: onPortalRelativeNavigate }) }) }));
};
const getImage = async (keyOrId, size, store) => {
    if (!keyOrId) {
        return undefined;
    }
    const imageAsset = await assetGetData(keyOrId, size, store);
    return {
        url: imageAsset.url,
        status: imageAsset.status,
        alt: imageAsset.alt,
        className: imageAsset.className,
        avatar: imageAsset.avatar,
    };
};
const getLanguageFromLocale = (locale) => locale.split('-')[0];
const resolveLocalizedAssetId = ({ legacyId, localizedMap, enabled, locale, defaultLocale, }) => {
    if (!enabled) {
        return legacyId;
    }
    if (!localizedMap || typeof localizedMap !== 'object') {
        return legacyId;
    }
    const activeLocale = locale || defaultLocale || 'en';
    const language = getLanguageFromLocale(activeLocale);
    if (localizedMap[activeLocale]) {
        return localizedMap[activeLocale];
    }
    if (language && localizedMap[language]) {
        return localizedMap[language];
    }
    if (activeLocale === defaultLocale) {
        return legacyId;
    }
    if (defaultLocale && localizedMap[defaultLocale]) {
        return localizedMap[defaultLocale];
    }
    if (defaultLocale !== 'en' && localizedMap.en) {
        return localizedMap.en;
    }
    return legacyId;
};
EmbeddedConversationWidget.getData = async ({ headerLogo, minimizedLogo, clientAIAnimatedProcessingIcon, agentAvatarImage, userAvatarImage, sendImage, }, store) => {
    console.log('EmbeddedConversationWidget.getData', {
        headerLogo,
        minimizedLogo,
        clientAIAnimatedProcessingIcon,
        agentAvatarImage,
        userAvatarImage,
        sendImage,
    });
    const [logoData, minimizedLogoData, clientAIAnimatedProcessingIconData, agentAvatarImageData, userAvatarImageData, sendImageData,] = await Promise.all([
        getImage(headerLogo, 'original', store),
        getImage(minimizedLogo, 'original', store),
        getImage(clientAIAnimatedProcessingIcon, 'original', store),
        getImage(agentAvatarImage, 'original', store),
        getImage(userAvatarImage, 'original', store),
        getImage(sendImage, 'original', store),
    ]);
    return {
        headerLogo: logoData,
        minimizedLogo: minimizedLogoData,
        clientAIAnimatedProcessingIcon: clientAIAnimatedProcessingIconData,
        agentAvatarImage: agentAvatarImageData,
        userAvatarImage: userAvatarImageData,
        sendImage: sendImageData,
    };
};
EmbeddedConversationWidget.defaultProps = {
    headerLogo: undefined,
    headerLogoLocalized: false,
    headerLogoL10N: undefined,
    minimizedLogo: undefined,
    minimizedLogoLocalized: false,
    minimizedLogoL10N: undefined,
    logoPosition: 'center',
    headerHeight: 50,
    menuButtonPosition: 'right',
    minimizeButtonPosition: 'left',
    closeButtonPosition: 'hide',
    windowStyle: {
        backgroundColor: 'color_secondary',
        borderColor: 'color_primary',
        borderRadius: 4,
        borderWidth: 0,
        color: 'color_font',
        margin: 0,
        padding: 4,
        shadow: 0,
    },
    headerStyle: {
        backgroundColor: 'color_secondary',
        borderColor: 'color_primary',
        borderRadius: 2,
        borderWidth: 0,
        color: 'color_font',
        margin: 0,
        padding: 4,
        shadow: 0,
    },
    headerControlsColor: 'color_primary',
    dividerStyle: {
        thickness: 0,
        color: 'color_primary',
    },
    bodyStyle: {
        backgroundColor: 'color_background',
        borderColor: 'color_primary',
        borderRadius: 2,
        borderWidth: 0,
        color: 'color_font',
        margin: 0,
        padding: 4,
        shadow: 0,
    },
    footerStyle: {
        backgroundColor: 'color_secondary',
        borderColor: 'color_primary',
        borderRadius: 2,
        borderWidth: 0,
        color: 'color_font',
        margin: 0,
        padding: 2,
        shadow: 0,
    },
    footerLinks: {
        title: '',
        links: [],
    },
    agentName: undefined,
    ...Conversation.defaultProps,
    showItemReferencesWithMessage: false,
};
EmbeddedConversationWidget.meta = {
    group: 'portal',
    name: 'Embedded AI Conversation',
    sort: 40,
    description: 'Conversation widget with header and footer for inclusion in 3rd party sites.',
    props: [
        {
            key: 'title',
            name: 'Window Title',
            type: 'text',
            description: 'Title for dialog.',
        },
        {
            key: 'titlePosition',
            name: 'Title Position',
            type: 'array',
            items: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
            ],
            description: 'Position of title in the header.',
        },
        {
            key: 'headerLogo',
            name: 'Logo Image',
            type: 'imageAsset',
            description: 'Logo to show in header.',
        },
        {
            key: 'headerLogoLocalized',
            name: 'Localize Header Logo',
            type: 'boolean',
            description: 'Use localized header logo when available.',
            guardedFields: {
                legacyKey: 'headerLogo',
                localizedKey: 'headerLogoL10N',
            },
        },
        {
            key: 'headerLogoL10N',
            name: 'Header Logo (Localized)',
            type: 'localizedImageAsset',
            description: 'Localized logo image for the header.',
        },
        {
            key: 'minimizedLogo',
            name: 'Minimized Logo Image',
            type: 'imageAsset',
            description: 'Logo to show when the widget is minimized.',
        },
        {
            key: 'minimizedLogoLocalized',
            name: 'Localize Minimized Logo',
            type: 'boolean',
            description: 'Use localized minimized logo when available.',
            guardedFields: {
                legacyKey: 'minimizedLogo',
                localizedKey: 'minimizedLogoL10N',
            },
        },
        {
            key: 'minimizedLogoL10N',
            name: 'Minimized Logo (Localized)',
            type: 'localizedImageAsset',
            description: 'Localized logo image for minimized widget.',
        },
        {
            key: 'logoPosition',
            name: 'Logo Position',
            type: 'array',
            items: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
            ],
            description: 'Position of logo in the header.',
        },
        {
            key: 'headerHeight',
            name: 'Header Height',
            type: 'number',
            description: 'Height of the header in pixels.',
            min: 20,
            max: 300,
        },
        {
            key: 'menuButtonPosition',
            name: 'Menu Button Position',
            type: 'array',
            items: [
                { value: 'left', label: 'Left' },
                { value: 'right', label: 'Right' },
                { value: 'hide', label: 'Hide' },
            ],
            description: 'Position of menu button in the header.',
        },
        {
            key: 'minimizeButtonPosition',
            name: 'Minimize Button Position',
            type: 'array',
            items: [
                { value: 'left', label: 'Left' },
                { value: 'right', label: 'Right' },
                { value: 'hide', label: 'Hide' },
            ],
            description: 'Position of minimize button in the header.',
        },
        {
            key: 'closeButtonPosition',
            name: 'Close Button Position',
            type: 'array',
            items: [
                { value: 'left', label: 'Left' },
                { value: 'right', label: 'Right' },
                { value: 'hide', label: 'Hide' },
            ],
            description: 'Position of close button in the header.',
        },
        {
            key: 'windowStyle',
            name: 'Window Style',
            type: 'boxStyle',
            description: 'Style of window.',
            group: 'style',
        },
        {
            key: 'bodyStyle',
            name: 'Body Style',
            type: 'boxStyle',
            description: 'Style of the body area of the chat.',
            group: 'style',
        },
        {
            key: 'headerStyle',
            name: 'Header Style',
            type: 'boxStyle',
            description: 'Style of window header.',
            group: 'style',
        },
        ...[
            ['headerLogo', 'Header Logo'],
            ['minimizedLogo', 'Minimized Logo'],
            ['sendImage', 'Send Image'],
        ].flatMap(([key, name]) => [
            {
                key: `welcomeScreenShellStyle.${key}.width`,
                name: `${name} Width (CSS)`,
                type: 'text',
                cssLength: true,
                group: 'style',
            },
            {
                key: `welcomeScreenShellStyle.${key}.height`,
                name: `${name} Height (CSS)`,
                type: 'text',
                cssLength: true,
                group: 'style',
            },
        ]),
        ...[
            ['headerLogo', 'Header Logo'],
            ['minimizedLogo', 'Minimized Logo'],
        ].map(([key, name]) => ({
            key: `welcomeScreenShellStyle.${key}.objectFit`,
            name: `${name} Fit`,
            type: 'selector',
            menuItems: ['contain', 'cover', 'fill', 'none', 'scale-down'].map((value) => ({ value, label: value })),
            group: 'style',
        })),
        {
            key: 'welcomeScreenShellStyle.sendImage.padding',
            name: 'Send Image Padding (CSS, e.g. 0 or 8px)',
            type: 'text',
            cssLength: true,
            group: 'style',
        },
        {
            key: 'headerControlsColor',
            name: 'Header Controls Color',
            type: 'color',
            description: 'Color of header controls.',
            group: 'style',
        },
        {
            key: 'dividerStyle.color',
            name: 'Divider Color',
            type: 'color',
            description: 'Color of divider between header and body.',
            group: 'style',
        },
        {
            key: 'dividerStyle.thickness',
            name: 'Divider Thickness',
            type: 'number',
            description: 'Thickness of divider between header and body.',
            group: 'style',
        },
        {
            key: 'footerStyle',
            name: 'Footer Style',
            type: 'boxStyle',
            description: 'Style of window footer.',
            group: 'style',
        },
        {
            key: 'footerText',
            name: 'Footer Text',
            type: 'text',
            description: 'Optional footer text when welcome HTML is configured.',
        },
        {
            key: 'footerLinks',
            name: 'Footer Links',
            type: 'linkList',
            allowSupportAction: true,
            description: 'Footer links',
        },
        {
            key: 'showItemReferencesWithMessage',
            name: 'Show Item References With Message',
            type: 'boolean',
            description: 'Displays relevent content next to the message.',
        },
        {
            key: 'agentName',
            name: 'Agent Name',
            type: 'text',
            description: 'Optional agent identifier for AI reporting.',
        },
        {
            key: 'productPromptHeader',
            name: 'Product Prompt Header',
            type: 'text',
            description: 'Header message to display above product-specific prompts.',
            default: 'Have questions about this product?',
        },
        ...sharedConversationMeta,
    ],
    defaultConfig: {
        type: 'component',
        component: 'EmbeddedConversationWidget',
        props: EmbeddedConversationWidget.defaultProps,
    },
};
export default observer(EmbeddedConversationWidget);
//# sourceMappingURL=EmbeddedConversationWidget.js.map